import type { CatalogItem, Theme } from "@albertine/types/openapi/lmt";
import { ThemePreferenceTextMap, themeLocationToText } from "@albertine/types/theme";
import classNames from "classnames";
import { isEmpty, isString } from "../../common/utils/string.util";
import ArticleThumbnail from "../CatalogBeta/ArticleThumbnail";
import Button from "../Common/Buttons";
import { Flex } from "../Common/Flex";
import Icon, { PictogramIcon } from "../Common/Icon";
import Label from "../Common/Label";
import Tag from "../Common/Tag";
import Tooltip from "../Common/Tooltip";
import "./ThemeView.css";

export function createThemeAnchor(theme: Pick<Theme, "id">) {
    return `theme__${theme.id}`;
}

type ThemeViewProps = {
    theme: Theme;
    articles: CatalogItem[];
    showTargeting: boolean;
    groupedTheme: boolean;
    onThemeEditClick: (() => void) | undefined;
    onArticleClick: ((articleId: string) => void) | undefined;
};

function ThemeView(props: ThemeViewProps) {
    const {
        theme,
        articles,
        showTargeting,
        groupedTheme,
        onThemeEditClick,
        onArticleClick,
    } = props;

    const targeting = showTargeting ? (
        <>
            {theme.preferences.map((preference) => (
                <Tag.Medium.T90 key={preference}>
                    {ThemePreferenceTextMap[preference] || preference}
                </Tag.Medium.T90>
            ))}

            {theme.location.map((location) => (
                <Tag.Medium.T90 key={themeLocationToText(location)}>
                    {themeLocationToText(location)}
                </Tag.Medium.T90>
            ))}

            {theme.location.length === 0 && <Tag.Medium.T90>Global</Tag.Medium.T90>}
        </>
    ) : null;

    return (
        <Flex.Column
            id={createThemeAnchor(theme)}
            gap="large"
            className={classNames("theme-view", {
                "theme-view-group": groupedTheme,
                "theme-view--editable": !!onThemeEditClick,
            })}
        >
            <Flex.Row gap="medium">
                <Flex.Row gap="medium" wrap="wrap">
                    <h3>{theme.name}</h3>
                    {!isEmpty(theme.publishedAt) && (
                        <Tooltip label="Published" placement="bottom">
                            <PictogramIcon.Check.Small.P90 />
                        </Tooltip>
                    )}
                    <Tag.Medium.N90>{articles.length}</Tag.Medium.N90>

                    {targeting}
                </Flex.Row>

                {onThemeEditClick && (
                    <Button.Ghost
                        className="theme-view-edit-button"
                        onClick={onThemeEditClick}
                    >
                        <Icon.Edit.Medium.N10 />
                    </Button.Ghost>
                )}
            </Flex.Row>
            <Flex.Row
                className={classNames("theme-view__article-container", {
                    "theme-view__article-container--small": !!groupedTheme,
                })}
                alignItems="start"
            >
                {articles.map((article) => {
                    if (isString(article)) {
                        return (
                            <Flex.Column
                                key={`${theme.id}-${article}`}
                                className={classNames(
                                    "theme-view__article",
                                    "theme-view__article--missing",
                                    {
                                        "theme-view__article--small": !!groupedTheme,
                                        "theme-view__article--viewable":
                                            !!onArticleClick,
                                    },
                                )}
                                alignItems="center"
                                onClick={() => {
                                    onArticleClick
                                        ? onArticleClick(article)
                                        : undefined;
                                }}
                            >
                                <Label.Small.Light.N60>
                                    (Article {article} not found)
                                </Label.Small.Light.N60>
                            </Flex.Column>
                        );
                    }

                    return (
                        <ArticleThumbnail
                            key={article.name}
                            size={groupedTheme ? "S" : "M"}
                            className={classNames("theme-view__article", {
                                "theme-view__article--small": !!groupedTheme,
                                "theme-view__article--viewable": !!onArticleClick,
                                "theme-view__article--unpublished": !article.published,
                            })}
                            article={article}
                            onClick={() => {
                                onArticleClick
                                    ? onArticleClick(article.lmtId)
                                    : undefined;
                            }}
                        />
                    );
                })}
            </Flex.Row>
        </Flex.Column>
    );
}

export default ThemeView;
